import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  Inject,
  inject,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  computed,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Icons } from '../../../shared/helpers/icons';
import { BuildingLevel } from '../../../shared/models/building';
import { BuildingService } from '../../../shared/services/building.service';
import { BuildingContextService } from '../../../shared/services/building-context.service';
import {
  BuildingLayer,
  StreetviewService
} from '../../../shared/services/streetview.service';
import { loadGoogleMaps } from '../../../shared/helpers/google-maps-loader';
import { SpinnerComponent } from '../../spinner/spinner.component';

interface RenderSummary {
  total: number;
  rendered: number;
  byType: Record<string, number>;
  truncated?: boolean;
  maxFeatures?: number;
}

@Component({
  selector: 'app-maps',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, SpinnerComponent],
  templateUrl: './maps.component.html',
  styleUrl: './maps.component.css'
})
export class MapsComponent implements AfterViewInit, OnDestroy {
  icons = Icons;

  readonly selectedBuilding = computed(() => this.buildingContext.selected());

  geojsonFile = signal<File | null>(null);
  parsing = signal(false);
  loadingFromBuilding = signal(false);
  // True only during the very first fetch of a (building, level) session —
  // shows the full-screen SpinnerComponent. Cleared after the first response
  // so subsequent pan/zoom-driven fetches happen silently in the background.
  initialLoading = signal(false);
  summary = signal<RenderSummary | null>(null);
  source = signal<'file' | 'building' | null>(null);

  // Levels for the dropdown — driven reactively by the navbar building pick.
  levels = signal<BuildingLevel[]>([]);
  loadingLevels = signal(false);
  selectedLevelId = signal<number | null>(null);

  // Layer toggles — only populated when source = 'building'.
  layers = signal<BuildingLayer[]>([]);
  visibleLayerIds = signal<Set<number>>(new Set());

  fileLabel = computed(() => this.geojsonFile()?.name ?? 'No file selected');

  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('mapEl', { static: true }) mapEl!: ElementRef<HTMLDivElement>;

  private map: google.maps.Map | null = null;
  private dataLayer: google.maps.Data | null = null;
  private infoWindow: google.maps.InfoWindow | null = null;

  // Bbox + simplify scaling: cache rendered features by feature_id so a pan
  // back to a previously-loaded region is instant (no flicker, no refetch
  // duplicate inserts), and a debounced `idle` listener fires the next request.
  private renderedFeatureIds = new Set<number>();
  private idleListener: google.maps.MapsEventListener | null = null;
  private idleDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private inflightRequest: { unsubscribe: () => void } | null = null;
  // Set on the very first fetch of a (building, level) session so the
  // response handler knows it's allowed to auto-center on building_center.
  // Subsequent fetches preserve the user's pan/zoom.
  private autoCenterOnNextResponse = false;
  // Auto-load gating. `mapReady` flips true once Google Maps + the data
  // layer are constructed; the effect waits for this so it doesn't fire
  // before ngAfterViewInit. `lastLoadedKey` ("buildingId-levelId") dedupes
  // so picking the same combo twice doesn't trigger a second load.
  private mapReady = signal(false);
  private lastLoadedKey = '';
  // Skip identical-bbox fetches. Defends against the map firing redundant
  // `idle` events (style re-applies, layout shifts, etc.) where the viewport
  // didn't actually move. Without this we ended up in a fetch loop.
  private lastFetchedBbox = '';
  // Debounce window for the map `idle` event — long enough that a continuous
  // pan-and-zoom only fires once after the user releases.
  private static readonly IDLE_DEBOUNCE_MS = 350;
  // Default zoom we snap to when auto-centering on a building's point. Street
  // level — gives the user enough context to see floor-plan polygons + pano
  // pins right away. They can zoom out from there.
  private static readonly AUTO_CENTER_ZOOM = 18;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private buildingContext: BuildingContextService,
    private buildingService: BuildingService,
    private streetview: StreetviewService,
    private toastr: ToastrService
  ) {
    // Re-apply Data layer styles whenever the visible-layer set changes.
    // The style function reads `visibleLayerIds()` and returns `visible: false`
    // for hidden layers; calling setStyle with the same function re-evaluates
    // every feature against the current set.
    effect(() => {
      this.visibleLayerIds(); // signal dependency
      this.applyStyles();
    });

    // Auto-load when arriving on the maps page (or switching to a different
    // building/level via the navbar). Mirrors v2.nside.io's UX: pick a
    // building, see its features. No "Load from building" click needed.
    //
    // Guards:
    //   - waits for mapReady so we don't run before Google Maps initializes
    //   - skips file-drop sessions (source === 'file') so a user previewing
    //     a GeoJSON isn't clobbered by a stray navbar pick
    //   - dedupes via lastLoadedKey so re-running the effect for unrelated
    //     signal changes (e.g. layers list refreshing) doesn't refetch
    effect(() => {
      const building = this.buildingContext.selected();
      const levelId = this.selectedLevelId();
      const ready = this.mapReady();

      if (!building || levelId == null || !ready) return;
      if (this.source() === 'file') return;

      const key = `${building.id}-${levelId}`;
      if (this.lastLoadedKey === key) return;
      this.lastLoadedKey = key;

      // queueMicrotask defers loadFromBuilding past the current change-
      // detection cycle so signals it touches (loadingFromBuilding, source,
      // etc.) don't trigger an "expression has changed after it was checked"
      // warning in dev mode.
      queueMicrotask(() => this.loadFromBuilding());
    });

    // Reactive level pipeline — same shape as bulk-upload-360. Switches off
    // any in-flight request when the user picks a different building.
    // Defaults `selectedLevelId` to the first level so the user can hit
    // "Load from building" immediately without an extra click.
    toObservable(this.buildingContext.selected)
      .pipe(
        tap(building => {
          this.selectedLevelId.set(null);
          this.levels.set([]);
          this.loadingLevels.set(!!building);
        }),
        switchMap(building =>
          building
            ? this.buildingService.getLevels(building.id).pipe(
                catchError(() => {
                  this.toastr.error('Failed to load levels.');
                  return of<BuildingLevel[]>([]);
                })
              )
            : EMPTY
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(levels => {
        this.levels.set(levels);
        this.loadingLevels.set(false);
        if (levels.length > 0) {
          this.selectedLevelId.set(levels[0].id);
          return;
        }
        // No levels — warn the user and leave selectedLevelId null so the
        // auto-load effect's `levelId == null` guard short-circuits and no
        // /features request goes out.
        const building = this.buildingContext.selected();
        if (building) {
          this.toastr.warning(
            `${building.name} has no lengineering els available — pick another building or check the building's setup.`
          );
        }
      });
  }

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      await loadGoogleMaps();
    } catch (e: any) {
      this.toastr.error('Could not load Google Maps: ' + (e?.message || 'unknown'));
      return;
    }

    this.map = new google.maps.Map(this.mapEl.nativeElement, {
      center: { lat: 33.749, lng: -84.388 }, // Atlanta-ish until data loads
      zoom: 5,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      streetViewControl: false,
      fullscreenControl: true,
      mapTypeControl: true
    });

    this.infoWindow = new google.maps.InfoWindow();

    this.dataLayer = this.map.data;
    this.applyStyles();
    this.dataLayer.addListener('click', (ev: google.maps.Data.MouseEvent) =>
      this.openFeaturePopup(ev)
    );

    // Signal that the map + data layer are ready. The auto-load effect in the
    // constructor waits on this — without it, an effect that fires before
    // ngAfterViewInit would run loadFromBuilding() with `this.map === null`.
    this.mapReady.set(true);
  }

  ngOnDestroy(): void {
    this.detachIdleListener();
    this.cancelInflightFetch();
    this.infoWindow?.close();
    this.dataLayer = null;
    this.infoWindow = null;
    this.map = null;
  }

  // ---- Bbox + simplify driver --------------------------------------------
  // Match the v2 map-viewer pattern: fetch on map `idle`, debounced, scoped
  // to the current viewport. Features cached by feature_id so panning back is
  // instant. Geometry simplification tolerance scales with current zoom so
  // polygon vertex counts shrink at low zoom without hurting quality at high.

  private detachIdleListener(): void {
    if (this.idleListener) {
      this.idleListener.remove();
      this.idleListener = null;
    }
    if (this.idleDebounceTimer) {
      clearTimeout(this.idleDebounceTimer);
      this.idleDebounceTimer = null;
    }
  }

  private cancelInflightFetch(): void {
    if (this.inflightRequest) {
      this.inflightRequest.unsubscribe();
      this.inflightRequest = null;
    }
  }

  /** Tolerance in degrees for ST_Simplify, picked from current zoom.
   *  ~0.000001 at street zoom (full fidelity), ~0.01 at country zoom. */
  private simplifyToleranceForZoom(zoom: number): number {
    if (zoom >= 18) return 0;          // street level — no simplification
    if (zoom >= 16) return 0.000005;
    if (zoom >= 13) return 0.00005;
    if (zoom >= 10) return 0.0005;
    if (zoom >= 7)  return 0.005;
    return 0.05;                        // country / continent
  }

  /** Returns "swLng,swLat,neLng,neLat" for the map's current viewport. */
  private currentBboxParam(): string | null {
    const b = this.map?.getBounds();
    if (!b) return null;
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    return `${sw.lng()},${sw.lat()},${ne.lng()},${ne.lat()}`;
  }

  /** Fetches viewport-scoped features and merges them into the cache. */
  private fetchViewportFeatures(): void {
    const building = this.buildingContext.selected();
    const levelId = this.selectedLevelId();
    if (!building || levelId == null || !this.map || !this.dataLayer) return;

    const bbox = this.currentBboxParam();
    if (!bbox) return;

    // Skip if the viewport hasn't actually moved since the last fetch.
    // Belt-and-suspenders against redundant `idle` events from layout shifts,
    // style re-applies, or DOM-overlay toggles.
    if (bbox === this.lastFetchedBbox) return;
    this.lastFetchedBbox = bbox;

    this.cancelInflightFetch();
    this.loadingFromBuilding.set(true);

    const zoom = this.map.getZoom() ?? 12;
    this.inflightRequest = this.streetview
      .getFeaturesForBuilding(building.id, levelId, {
        bbox,
        simplify: this.simplifyToleranceForZoom(zoom)
      })
      .subscribe({
        next: data => {
          this.loadingFromBuilding.set(false);
          this.inflightRequest = null;
          // Layers list is stable per (building, level) — only set the first
          // time so unchecking a layer mid-session doesn't get clobbered.
          //
          // Include EVERY layer in the visible set — `feature_count` here is
          // per-viewport (post-bbox), not per-building. Filtering by it would
          // mute legitimate layers whose features happen to be outside the
          // current viewport on the first response (e.g. right after a
          // building switch, before auto-center has moved the map).
          if (this.layers().length === 0) {
            this.layers.set(data.layers || []);
            this.visibleLayerIds.set(new Set((data.layers || []).map(l => l.id)));
          }
          this.mergeFeatures(data.features, {
            truncated: data.truncated,
            maxFeatures: data.max_features
          });

          // First-fetch auto-center: snap the viewport to the building's
          // stored centroid so the user doesn't have to hunt for it. This
          // re-centers will fire another `idle` → another bbox fetch for the
          // new viewport — that's intentional, not a bug.
          if (this.autoCenterOnNextResponse) {
            this.autoCenterOnNextResponse = false;
            if (data.building_center && this.map) {
              // Centering changes the bbox, which we WANT the next idle
              // listener to act on. Clear the dedupe so the post-recenter
              // fetch isn't suppressed.
              this.lastFetchedBbox = '';
              this.map.setCenter({
                lat: data.building_center.lat,
                lng: data.building_center.lng
              });
              this.map.setZoom(MapsComponent.AUTO_CENTER_ZOOM);
            }
          }

          // Clear the full-screen spinner — even if more idle-driven fetches
          // are queued, the user has now seen the building positioned and
          // initial features rendered. Subsequent fetches stay silent.
          this.initialLoading.set(false);
        },
        error: err => {
          this.loadingFromBuilding.set(false);
          this.initialLoading.set(false);
          this.inflightRequest = null;
          const msg = err?.error?.description || err?.message || 'request failed';
          this.toastr.error('Could not load features: ' + msg);
        }
      });
  }

  /** Adds only feature_ids we haven't rendered yet. Skips duplicates so
   *  overlapping fetches (zoom out then pan) don't double-stack overlays. */
  private mergeFeatures(
    data: any,
    extras?: { truncated?: boolean; maxFeatures?: number }
  ): void {
    if (!this.dataLayer) return;

    const features: any[] = Array.isArray(data?.features) ? data.features : [];
    const fresh: any[] = [];
    for (const f of features) {
      const id = f?.properties?.feature_id;
      if (id == null) continue;
      if (this.renderedFeatureIds.has(id)) continue;
      this.renderedFeatureIds.add(id);
      fresh.push(f);
    }

    if (fresh.length > 0) {
      this.dataLayer.addGeoJson({ type: 'FeatureCollection', features: fresh });
    }

    // Summary reflects the cumulative cache, not just this batch.
    const byType: Record<string, number> = {};
    this.dataLayer.forEach(f => {
      const t = f.getGeometry()?.getType();
      if (!t) return;
      byType[t] = (byType[t] || 0) + 1;
    });
    this.summary.set({
      total: this.renderedFeatureIds.size,
      rendered: this.renderedFeatureIds.size,
      byType,
      truncated: extras?.truncated,
      maxFeatures: extras?.maxFeatures
    });

    if (extras?.truncated) {
      this.toastr.info(
        `Viewport hit the ${extras.maxFeatures}-feature cap — zoom in or filter by layer for full coverage here.`
      );
    }
  }

  // ---- File drop ----
  onGeoJsonChange(ev: Event): void {
    const file = (ev.target as HTMLInputElement).files?.[0] ?? null;
    this.geojsonFile.set(file);
    this.summary.set(null);
    if (file) {
      this.source.set('file');
      this.layers.set([]);
      this.visibleLayerIds.set(new Set());
      this.loadFile(file);
    }
  }

  private async loadFile(file: File): Promise<void> {
    if (!this.map || !this.dataLayer) {
      this.toastr.warning('Map is still initializing — try again in a moment.');
      return;
    }
    this.parsing.set(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      this.renderFeatureCollection(data);
    } catch (err: any) {
      this.toastr.error('Could not parse GeoJSON: ' + (err?.message || 'invalid file'));
    } finally {
      this.parsing.set(false);
    }
  }

  // ---- Load from API ----
  // Switches the map into "live, viewport-scoped" mode — every pan/zoom kicks
  // off a debounced bbox fetch. Cumulative cache means the first fetch shows
  // initial features, panning back to a previously-loaded area is instant,
  // and a building with 50K+ features only ships ~50–500 rows per request.
  loadFromBuilding(): void {
    const building = this.buildingContext.selected();
    const levelId = this.selectedLevelId();
    if (!building) {
      this.toastr.error('Pick a building from the top navbar first.');
      return;
    }
    if (!this.map || !this.dataLayer) {
      this.toastr.warning('Map is still initializing — try again in a moment.');
      return;
    }

    // Reset cache + UI state for this (building, level) session.
    this.dataLayer.forEach(f => this.dataLayer!.remove(f));
    this.renderedFeatureIds.clear();
    this.summary.set(null);
    this.geojsonFile.set(null);
    this.source.set('building');
    this.layers.set([]);
    this.visibleLayerIds.set(new Set());

    // Tell the next response handler it's allowed to auto-center the map
    // on `building_center`. Cleared after the first response lands so
    // subsequent pans/zooms don't snap back.
    this.autoCenterOnNextResponse = true;
    // Show the full-screen spinner until the first response arrives. The
    // existing in-map overlay handles file-drop parsing; idle-driven fetches
    // after the first one happen silently to avoid blocking the user.
    this.initialLoading.set(true);
    // Reset the bbox dedupe so the very first viewport-scoped fetch always
    // fires (the previous session's bbox might happen to match the current).
    this.lastFetchedBbox = '';

    // Detach any previous idle listener (different building/level was loaded).
    this.detachIdleListener();
    this.cancelInflightFetch();

    // Attach the debounced idle listener. Map fires `idle` after every pan or
    // zoom completes; the debounce collapses rapid-fire events (e.g. pinch
    // zoom on touch) into one trailing fetch.
    this.idleListener = this.map.addListener('idle', () => {
      if (this.idleDebounceTimer) clearTimeout(this.idleDebounceTimer);
      this.idleDebounceTimer = setTimeout(
        () => this.fetchViewportFeatures(),
        MapsComponent.IDLE_DEBOUNCE_MS
      );
    });

    // Trigger the first fetch immediately — the user just clicked Load From
    // Building and expects to see features without having to nudge the map.
    this.fetchViewportFeatures();
  }

  // ---- Layer toggle ----
  toggleLayer(layerId: number): void {
    const next = new Set(this.visibleLayerIds());
    if (next.has(layerId)) next.delete(layerId);
    else next.add(layerId);
    this.visibleLayerIds.set(next);
  }

  isLayerVisible(layerId: number): boolean {
    return this.visibleLayerIds().has(layerId);
  }

  showAllLayers(): void {
    // Include every layer regardless of `feature_count` — the count is
    // per-viewport, not per-building, so filtering by it would silently hide
    // layers whose features are outside the current bbox.
    this.visibleLayerIds.set(new Set(this.layers().map(l => l.id)));
  }

  hideAllLayers(): void {
    this.visibleLayerIds.set(new Set());
  }

  // ---- Render ----
  private renderFeatureCollection(
    data: any,
    extras?: { truncated?: boolean; maxFeatures?: number }
  ): void {
    if (!this.map || !this.dataLayer) return;

    // Wipe any previously loaded features.
    this.dataLayer.forEach(f => this.dataLayer!.remove(f));

    const features: any[] = Array.isArray(data?.features)
      ? data.features
      : data?.type === 'Feature'
      ? [data]
      : [];

    const byType: Record<string, number> = {};
    const valid: any[] = [];
    for (const f of features) {
      const t = f?.geometry?.type;
      if (!t) continue;
      byType[t] = (byType[t] || 0) + 1;
      valid.push(f);
    }

    if (valid.length > 0) {
      this.dataLayer.addGeoJson({ type: 'FeatureCollection', features: valid });
    }

    this.summary.set({
      total: features.length,
      rendered: valid.length,
      byType,
      truncated: extras?.truncated,
      maxFeatures: extras?.maxFeatures
    });

    if (valid.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      this.dataLayer.forEach(f => {
        const geom = f.getGeometry();
        if (!geom) return;
        geom.forEachLatLng(ll => bounds.extend(ll));
      });
      if (!bounds.isEmpty()) this.map.fitBounds(bounds, 60);
    } else if (features.length === 0) {
      this.toastr.warning('No features to render.');
    }

    if (extras?.truncated) {
      this.toastr.info(
        `Response truncated at ${extras.maxFeatures} features. Filter by level or layer to narrow it.`
      );
    }
  }

  // Style callback runs per feature; uses the current visibleLayerIds() to
  // hide features whose layer is unchecked. For file-drop sources (no layers
  // metadata), every feature is visible.
  private applyStyles(): void {
    if (!this.dataLayer) return;
    const visibleIds = this.visibleLayerIds();
    const hasLayers = this.layers().length > 0;

    this.dataLayer.setStyle(feature => {
      const layerId = feature.getProperty('layer_id') as number | null | undefined;
      const visible = !hasLayers || layerId == null || visibleIds.has(Number(layerId));

      const geom = feature.getGeometry();
      const type = geom?.getType();

      const base: google.maps.Data.StyleOptions = {
        visible,
        clickable: true
      };

      if (type === 'Point' || type === 'MultiPoint') {
        return { ...base, icon: this.pinIcon() };
      }
      if (type === 'LineString' || type === 'MultiLineString') {
        return { ...base, strokeColor: '#f15f3e', strokeWeight: 3, strokeOpacity: 0.9 };
      }
      if (type === 'Polygon' || type === 'MultiPolygon') {
        return {
          ...base,
          fillColor: '#f15f3e',
          fillOpacity: 0.25,
          strokeColor: '#7a2410',
          strokeWeight: 1.5,
          strokeOpacity: 0.9
        };
      }
      return base;
    });
  }

  // ---- Popup ----
  private openFeaturePopup(ev: google.maps.Data.MouseEvent): void {
    if (!this.infoWindow || !this.map) return;
    const feature = ev.feature;
    const props: Record<string, any> = {};
    feature.forEachProperty((value, name) => {
      props[name] = value;
    });
    let coords: [number, number] | undefined;
    const geom = feature.getGeometry();
    if (geom?.getType() === 'Point') {
      const ll = (geom as google.maps.Data.Point).get();
      coords = [ll.lng(), ll.lat()];
    } else if (ev.latLng) {
      coords = [ev.latLng.lng(), ev.latLng.lat()];
    }
    this.infoWindow.setContent(this.buildPopupHtml(props, coords));
    this.infoWindow.setPosition(ev.latLng);
    this.infoWindow.open({ map: this.map });
  }

  private buildPopupHtml(props: Record<string, any>, coords?: [number, number]): string {
    const rows: string[] = [];
    const interesting = [
      'feature_id',
      'layer_id',
      'level_id',
      'geo_type',
      'streetview_id',
      'label',
      'is_entrance',
      'is_path_blocked',
      'heading',
      // legacy file-drop properties:
      'point_id',
      'id',
      'level',
      'name'
    ];
    for (const k of interesting) {
      if (props[k] !== undefined && props[k] !== null && props[k] !== '') {
        rows.push(`<div><b>${escapeHtml(k)}:</b> ${escapeHtml(String(props[k]))}</div>`);
      }
    }
    const extras = Object.keys(props)
      .filter(k => !interesting.includes(k))
      .slice(0, 6);
    for (const k of extras) {
      const v = props[k];
      if (v === null || v === undefined || v === '') continue;
      const display =
        typeof v === 'object' ? JSON.stringify(v).slice(0, 120) : String(v);
      rows.push(`<div><b>${escapeHtml(k)}:</b> ${escapeHtml(display)}</div>`);
    }
    if (coords) {
      rows.push(
        `<div style="color:#666; margin-top:4px;">[${coords[1].toFixed(6)}, ${coords[0].toFixed(6)}]</div>`
      );
    }
    return rows.length ? `<div style="font-size:12px;">${rows.join('')}</div>` : '<i>(no properties)</i>';
  }

  // Inline orange SVG marker so we don't ship binary assets.
  private pinIcon(): google.maps.Icon {
    const svg =
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28">' +
      '<path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 7 12 8 12s8-6.75 8-12c0-4.42-3.58-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" ' +
      'fill="#f15f3e" stroke="#7a2410" stroke-width="0.75"/></svg>';
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(28, 28),
      anchor: new google.maps.Point(14, 26)
    };
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
